import { useState } from 'react';

import Tippy from '@tippyjs/react';
import { useRecoilValue } from 'recoil';

import AvatarGroup from 'components/shared/AvatarGroup/AvatarGroup';
import Button from 'components/shared/Button/Button';
import REVIEW_ONBOARDING_STEP from 'components/shared/OnboardingWizard/components/Header/REVIEW_ONBOARDING_STEP';
import state from 'components/state';

import TaskDelegationModal from '../TaskDelegationModal/TaskDelegationModal';

import GoLiveDateBanner from './GoLiveDateBanner';

const Footer = ({
  delegations,
  activeSubstep,
  footerConfig = {},
  orchestratorData,
  wizardState = {},
  isAdmin,
  navigateToSubstep = () => {}
}) => {
  const user_state = useRecoilValue(state.userState);
  const [openDelegateModal, setOpenDelegateModal] = useState(false);
  const activeSubstepId = activeSubstep?.substep_id;
  const activeStepId = orchestratorData?.stepId;

  const substepAssignments =
    delegations?.substeps?.find((d) => d.substep_id == activeSubstepId)?.assignments || [];

  const stepAssignments =
    delegations?.steps?.find((d) => d.step_id == activeStepId)?.assignments || [];

  const assignments = [
    ...stepAssignments,
    ...substepAssignments.filter(
      (subAssignment) =>
        !stepAssignments.some((stepAssignment) => stepAssignment.user_id === subAssignment.user_id)
    )
  ];

  const isDelegationSubstep = activeSubstep?.code === 'onboarding__delegation__delegation';

  const isTermsAndConditionsSubstep =
    orchestratorData?.substepCode === 'onboarding__acknowledgements__terms_and_conditions';
  const isGoLiveDateSubstepCompleted =
    wizardState?.steps
      .flatMap((step) => step.substeps)
      .find(
        (substep) =>
          substep.code === 'onboarding__go_live_dates_and_trainings__go_live_dates_and_trainings'
      )?.status === 'Completed';
  const currentUserId = user_state?.user_id ?? user_state?.id;

  const delegationStep = delegations?.steps?.find((s) => s?.step_code === 'onboarding__delegation');

  const delegationStepAssignments = delegationStep?.assignments || [];

  const isUserAssignedToDelegationStep = delegationStepAssignments.some(
    (a) => a.user_id === currentUserId
  );

  const showDelegationButton =
    (currentUserId === wizardState?.instance?.assigned_to ||
      isAdmin ||
      isUserAssignedToDelegationStep) &&
    orchestratorData?.stepCode !== REVIEW_ONBOARDING_STEP.code;

  return (
    <>
      <div className="onboarding-footer relative z-[99] flex w-full items-center justify-between gap-4 border-x-0 border-b-0 border-solid border-t-neutral-200 px-5 py-4 ring-black">
        {isTermsAndConditionsSubstep && !isGoLiveDateSubstepCompleted && (
          <GoLiveDateBanner navigateToSubstep={navigateToSubstep} />
        )}
        {showDelegationButton && !isDelegationSubstep ? (
          assignments.length === 0 ? (
            <Button
              color="white"
              textClassName="text-neutral-700 text-sm"
              text="Delegate this task"
              iconRight="delegate-task"
              iconColor="neutral"
              onClick={() => setOpenDelegateModal(true)}
              iconClassName="Icon--neutral-900"
            />
          ) : (
            <div className="flex items-center gap-2">
              <label className="text-xs font-500 text-neutral-600">Task is delegated to</label>
              <div className="flex items-center rounded-3xl border border-solid border-neutral-300 p-0.5">
                <AvatarGroup
                  isBorder={false}
                  className="cursor-pointer shadow-none"
                  isCompact={true}
                  itemsToShow={3}
                  singleHoverPlacement="top"
                  simpleTooltip
                  items={assignments.map((a) => ({
                    id: a?.user_id,
                    fullName: `${a?.first_name || ''} ${a?.last_name || ''}`.trim()
                  }))}
                />
                <Tippy
                  content="Add or remove delegations"
                  className="tippy-dark"
                  placement="top"
                  arrow>
                  <div>
                    <Button
                      color="white"
                      className="h-fit px-1"
                      textClassName="text-neutral-500 text-sm"
                      iconRight="delegate-task"
                      iconColor="neutral"
                      onClick={() => setOpenDelegateModal(true)}
                    />
                  </div>
                </Tippy>
              </div>
            </div>
          )
        ) : assignments.length > 0 && !isDelegationSubstep ? (
          <div className="flex items-center gap-2">
            <label className="text-xs font-500 text-neutral-600">Task is delegated to</label>
            <div className="flex items-center rounded-3xl border border-solid border-neutral-300 p-0.5">
              <AvatarGroup
                isBorder={false}
                className="shadow-none"
                isCompact={true}
                itemsToShow={3}
                items={assignments.map((a) => ({
                  id: a?.user_id,
                  fullName: `${a?.first_name || ''} ${a?.last_name || ''}`.trim()
                }))}
              />
            </div>
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-4">
          {footerConfig?.customRightContent}
          {footerConfig?.goBackHandler && (
            <Button
              variant="tertiary"
              color="neutral"
              text="Back"
              onClick={footerConfig?.goBackHandler}
            />
          )}
          {(footerConfig.actions ?? []).map((action) => (
            <Button
              key={action.key || action.id || action.text}
              {...action}
              color={action?.color || 'primary'}
            />
          ))}
        </div>
      </div>
      <TaskDelegationModal
        instanceId={orchestratorData?.instanceId}
        step_ids={activeStepId ? [activeStepId] : []}
        substep_ids={activeSubstepId ? [activeSubstepId] : []}
        isOpen={openDelegateModal}
        handleClose={() => setOpenDelegateModal(false)}
        assignments={assignments}
      />
    </>
  );
};

export default Footer;
